import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Checkbox,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SyncIcon from "@mui/icons-material/Sync";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import axios from "axios";
import instance from "../../axios";
import Button from "../../Components/common/Button";
import { checkPrivileges } from "../../Helpers";
import ExpiryCard from "./ExpiryCard";
import "./NotificationsDrawer.scss";

const PAGE_LIMIT = 20;

const parseCount = (data) => {
  if (typeof data === "number") return data;
  return data?.count ?? data?.unreadCount ?? data?.unread ?? data?.total ?? 0;
};

const parseNotifications = (data) => {
  if (Array.isArray(data)) {
    return { items: data, total: data.length };
  }
  if (Array.isArray(data?.data)) {
    return {
      items: data.data,
      total: data.total ?? data.count ?? data.data.length,
    };
  }
  if (Array.isArray(data?.items)) {
    return { items: data.items, total: data.total ?? data.items.length };
  }
  return { items: [], total: 0 };
};

const getNotificationId = (notification) =>
  notification?.id ?? notification?.notificationId;

const DEFAULT_ALERT_COLOR = "#7c4dff";

const getNotificationTitle = (notification) =>
  notification?.name || "Notification";

const getNotificationMessage = (notification) =>
  notification?.description || "";

const getNotificationDate = (notification) =>
  notification?.createdAt ??
  notification?.date ??
  notification?.created ??
  notification?.timestamp ??
  "";

const isNotificationRead = (notification) => Boolean(notification?.markRead);

const getNotificationAlertColor = (notification) =>
  notification?.attributes?.alertColor || DEFAULT_ALERT_COLOR;

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return null;
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return null;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  if ([r, g, b].some((value) => Number.isNaN(value))) return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const EMPTY_EXPIRY_COUNTS = {
  complianceExpiry: 0,
  licenseExpiry: 0,
  insuranceExpiry: 0,
};

const EMPTY_EXPIRY_SUMMARY = {
  due: EMPTY_EXPIRY_COUNTS,
  overdue: EMPTY_EXPIRY_COUNTS,
};

const getNotificationTypeKey = (notification) =>
  `${notification?.type || ""} ${notification?.attributes?.functionalType || ""} ${notification?.name || ""}`.toLowerCase();

const NotificationIcon = ({ notification }) => {
  const typeKey = getNotificationTypeKey(notification);

  if (typeKey.includes("maintenance")) {
    return <BuildOutlinedIcon fontSize="small" />;
  }
  if (typeKey.includes("booking")) {
    return <EventNoteOutlinedIcon fontSize="small" />;
  }
  if (typeKey.includes("workorder") || typeKey.includes("work order")) {
    return <SyncIcon fontSize="small" />;
  }
  if (typeKey.includes("approved") || typeKey.includes("success")) {
    return <CheckCircleOutlineIcon fontSize="small" />;
  }
  return <InfoOutlinedIcon fontSize="small" />;
};

const NotificationsDrawer = ({ translate, onCountChange }) => {
  const navigate = useNavigate();
  const canViewVehicleExpiry = checkPrivileges("vehicle");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [readFilter, setReadFilter] = useState("2");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [expirySummary, setExpirySummary] = useState(EMPTY_EXPIRY_SUMMARY);
  const [expiryLoaded, setExpiryLoaded] = useState(false);

  const updateCount = useCallback(
    (count) => {
      setNotificationCount(count);
      if (onCountChange) {
        onCountChange(count);
      }
    },
    [onCountChange],
  );

  const fetchNotificationCount = useCallback(async () => {
    try {
      const data = await instance.get("api/notifications/count");
      updateCount(parseCount(data));
    } catch (error) {
      console.log("notification count error", error);
    }
  }, [updateCount]);

  const fetchExpirySummary = useCallback(async () => {
    if (!canViewVehicleExpiry) return;
    try {
      const data = await instance.get("/api/vehicles/expiry-summary");
      setExpirySummary({
        due: data?.due || EMPTY_EXPIRY_COUNTS,
        overdue: data?.overdue || EMPTY_EXPIRY_COUNTS,
      });
    } catch (error) {
      console.log("vehicle expiry summary error", error);
      setExpirySummary(EMPTY_EXPIRY_SUMMARY);
    } finally {
      setExpiryLoaded(true);
    }
  }, [canViewVehicleExpiry]);

  const fetchNotifications = useCallback(
    async ({ pageNumber = 1, append = false } = {}) => {
      const isLoadMore = append && pageNumber > 1;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const data = await instance.get(
          `api/notifications/get1?page=${pageNumber}&limit=${PAGE_LIMIT}&read=${readFilter}`,
        );
        const { items, total } = parseNotifications(data);
        setNotifications((prev) => (append ? [...prev, ...items] : items));
        setPage(pageNumber);
        setHasMore(
          total ? pageNumber * PAGE_LIMIT < total : items.length === PAGE_LIMIT,
        );
      } catch (error) {
        console.log("notifications list error", error);
        if (!append) {
          setNotifications([]);
        }
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [readFilter],
  );

  useEffect(() => {
    fetchNotificationCount();
  }, [fetchNotificationCount]);

  useEffect(() => {
    if (drawerOpen) {
      fetchNotifications({ pageNumber: 1, append: false });
    }
  }, [drawerOpen, readFilter, fetchNotifications]);

  useEffect(() => {
    if (drawerOpen) {
      fetchExpirySummary();
    }
  }, [drawerOpen, fetchExpirySummary]);

  const handleOpenDrawer = () => {
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setPage(1);
  };

  const handleReadFilterChange = (event) => {
    setReadFilter(event.target.value);
    setPage(1);
  };

  const refreshData = async () => {
    await Promise.all([
      fetchNotificationCount(),
      fetchNotifications({ pageNumber: 1, append: false }),
    ]);
  };

  const handleMarkAsRead = async (notificationId) => {
    if (!notificationId) return;

    try {
      await axios.get(`api/notifications/markread/${notificationId}`);
      await refreshData();
    } catch (error) {
      console.log("mark read error", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await axios.get("api/notifications/markreadall");
      await refreshData();
    } catch (error) {
      console.log("mark all read error", error);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleLoadMore = () => {
    fetchNotifications({ pageNumber: page + 1, append: true });
  };

  const handleExpiryCardClick = (tab) => {
    handleCloseDrawer();
    navigate(tab === "overdue" ? "/vehicle-expiry?tab=overdue" : "/vehicle-expiry");
  };

  return (
    <>
      <li className="notification-bell-item">
        <IconButton
          className="notification-bell-btn"
          onClick={handleOpenDrawer}
          size="small"
          aria-label={translate("events")}
        >
          <Badge
            badgeContent={notificationCount}
            color="error"
            max={99}
            invisible={!notificationCount}
          >
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </li>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        className="notifications-drawer"
        PaperProps={{ className: "notifications-drawer-paper" }}
      >
        <Box className="notifications-drawer-header">
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton size="small" onClick={handleCloseDrawer}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" className="notifications-drawer-title">
              {translate("events")}
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            className="notifications-drawer-actions"
          >
            <Typography
              component="button"
              className="mark-all-read-btn"
              onClick={handleMarkAllAsRead}
              disabled={markingAll}
            >
              {translate("markAllNotificationsAsRead")}
            </Typography>

            <FormControl size="small" className="notifications-filter">
              <Select
                value={readFilter}
                onChange={handleReadFilterChange}
                displayEmpty
              >
                <MenuItem value="0">{translate("allNotifications")}</MenuItem>
                <MenuItem value="1">{translate("readNotifications")}</MenuItem>
                <MenuItem value="2">
                  {translate("unreadNotifications")}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box className="notifications-drawer-body">
          {canViewVehicleExpiry ? (
            <Box className="expiry-summary">
              <Typography className="expiry-summary-title">
                {translate("fleetExpiry")}
              </Typography>
              <Box className="expiry-summary-cards">
                <ExpiryCard
                  title={translate("fleetExpiryDue")}
                  data={expirySummary.due}
                  loaded={expiryLoaded}
                  variant="due"
                  onClick={() => handleExpiryCardClick("due")}
                />
                <ExpiryCard
                  title={translate("fleetExpiryOverdue")}
                  data={expirySummary.overdue}
                  loaded={expiryLoaded}
                  variant="overdue"
                  onClick={() => handleExpiryCardClick("overdue")}
                />
              </Box>
            </Box>
          ) : null}

          {loading && !notifications.length ? (
            <Typography className="notifications-empty">
              {translate("sharedLoading")}
            </Typography>
          ) : null}

          {!loading && !notifications.length ? (
            <Typography className="notifications-empty">
              {translate("noNotifications")}
            </Typography>
          ) : null}

          {notifications.map((notification) => {
            const notificationId = getNotificationId(notification);
            const isRead = isNotificationRead(notification);
            const alertColor = getNotificationAlertColor(notification);
            const cardBackground = hexToRgba(alertColor, 0.1) || "#f7f4ff";
            const iconBackground = hexToRgba(alertColor, 0.18) || "#ede7ff";
            const borderColor =
              hexToRgba(alertColor, 0.25) || "rgba(0, 0, 0, 0.08)";

            return (
              <Box
                key={notificationId}
                className={`notification-card ${
                  isRead ? "notification-card--read" : ""
                }`}
                style={{
                  backgroundColor: cardBackground,
                  borderColor,
                }}
              >
                <Box
                  className="notification-card-icon"
                  style={{
                    backgroundColor: iconBackground,
                    color: alertColor,
                  }}
                >
                  <NotificationIcon notification={notification} />
                </Box>

                <Box className="notification-card-content">
                  <Typography
                    className="notification-card-title"
                    style={{ color: alertColor }}
                  >
                    {getNotificationTitle(notification)}
                  </Typography>
                  <Typography className="notification-card-message">
                    {getNotificationMessage(notification)}
                  </Typography>
                </Box>

                {!isRead ? (
                  <Checkbox
                    className="notification-card-checkbox"
                    size="small"
                    sx={{
                      color: alertColor,
                      "&.Mui-checked": { color: alertColor },
                    }}
                    onChange={() => handleMarkAsRead(notificationId)}
                  />
                ) : null}

                {getNotificationDate(notification) ? (
                  <Typography className="notification-card-date">
                    {getNotificationDate(notification)}
                  </Typography>
                ) : null}

                <Box
                  className="notification-card-accent"
                  style={{ backgroundColor: alertColor }}
                />
              </Box>
            );
          })}
        </Box>

        {hasMore ? (
          <Box className="notifications-drawer-footer">
            <Button
              variant="contained"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {translate("loadMore")}
            </Button>
          </Box>
        ) : null}
      </Drawer>
    </>
  );
};

export default NotificationsDrawer;
